import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getVariableTextEditor, } from '../VariableTextEditor';
/**
 * One focusable block in a list the author types into line by line. The
 * form's items and a conversation node's signals both use it; what differs
 * between them is the placeholder and the chips, and both come in as props.
 *   - Enter commits the block.
 *   - Backspace at the start of an empty block deletes it.
 *   - Tab / Shift+Tab move or nest, as the parent decides.
 *   - Multi-line paste splits into blocks.
 */
export function ItemBlock({ item, label, hasNext, inferring, placeholder, chips, icon, variables, registerRef, onTextChange, onCommit, onDeleteEmpty, onPasteSplit, onIndent, onOutdent, onFocusPrev, onFocusNext, onBlurBlock, onOpen, }) {
    const taRef = useRef(null);
    const [focused, setFocused] = useState(false);
    const depth = item.depth ?? 0;
    // Auto-grow: snap textarea height to its scrollHeight on every change.
    useLayoutEffect(() => {
        const el = taRef.current;
        if (!el)
            return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [item.text]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onCommit();
            return;
        }
        if (event.key === 'Tab') {
            event.preventDefault();
            if (event.shiftKey) {
                // Shift+Tab degrades to focus-prev once outdenting isn't possible,
                // so holding shift+tab walks backward through the list.
                if ((item.depth ?? 0) === 1)
                    onOutdent();
                else
                    onFocusPrev();
            }
            else {
                // Tab: caret at end + next item exists → focus next; otherwise
                // (no next item, or caret mid-text) → indent.
                const el = taRef.current;
                const atEnd = el != null &&
                    el.selectionStart === item.text.length &&
                    el.selectionEnd === item.text.length;
                if (atEnd && hasNext) {
                    onFocusNext();
                }
                else {
                    onIndent();
                }
            }
            return;
        }
        if (event.key === 'Backspace' &&
            item.text.length === 0 &&
            taRef.current?.selectionStart === 0 &&
            taRef.current?.selectionEnd === 0) {
            event.preventDefault();
            onDeleteEmpty();
        }
    }, [
        item.text,
        item.text.length,
        item.depth,
        hasNext,
        onCommit,
        onDeleteEmpty,
        onIndent,
        onOutdent,
        onFocusPrev,
        onFocusNext,
    ]);
    const handlePaste = useCallback((event) => {
        const text = event.clipboardData.getData('text');
        if (!text.includes('\n'))
            return;
        event.preventDefault();
        const lines = text
            .split('\n')
            .map(line => line.replace(/^\s*(?:\d+[.)]|[-*])\s*/, '').trim())
            .filter(Boolean);
        if (lines.length <= 1) {
            onTextChange(lines[0] ?? '');
            return;
        }
        onPasteSplit(lines);
    }, [onTextChange, onPasteSplit]);
    const showChrome = focused || inferring;
    // Indent for depth-1 sub-items; keep the chip row aligned under the text.
    const indentPx = depth * 20;
    // 22px number col + 4px gap, plus a 12px icon + 6px gap when there is one.
    const chipRowPaddingLeft = indentPx + 26 + (icon ? 18 : 0);
    const Editor = getVariableTextEditor();
    return (_jsxs("div", { className: cn('group py-0.5 rounded-sm transition-colors', focused ? 'bg-muted/50' : 'hover:bg-muted/30'), children: [_jsxs("div", { className: "flex items-start gap-1.5", style: { paddingLeft: indentPx }, children: [_jsxs("span", { className: cn('mt-1 text-[10px] font-medium select-none min-w-[22px] text-right tabular-nums', focused || inferring
                            ? 'text-foreground/70'
                            : 'text-muted-foreground'), children: [label, "."] }), icon && _jsx("span", { className: "mt-1.5 flex", children: icon }), _jsx(Editor, { textareaRef: el => {
                            taRef.current = el;
                            registerRef(el);
                        }, id: `form-item-${item.id}`, name: item.id, dataFormItemId: item.id, rows: 1, value: item.text, onValueChange: onTextChange, onKeyDown: handleKeyDown, onPaste: handlePaste, onFocus: () => setFocused(true), onBlur: () => {
                            setFocused(false);
                            onBlurBlock();
                        }, placeholder: placeholder, variables: variables, className: "flex-1 min-w-0 !min-h-0 resize-none border-none bg-transparent px-0 py-0.5 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground" }), _jsx("button", { type: "button", className: cn('p-0.5 mt-0.5 rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-opacity', showChrome ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'), title: "Open item details", "aria-label": "Open item details", onMouseDown: e => {
                            // Don't steal focus from the textarea while clicking the chevron.
                            e.preventDefault();
                        }, onClick: onOpen, children: _jsx(ChevronRight, { className: "h-3.5 w-3.5" }) })] }), (inferring || item.text.trim().length > 0) && (_jsx("div", { className: "flex flex-wrap items-center gap-1.5 min-h-[14px]", style: { paddingLeft: chipRowPaddingLeft }, children: inferring ? (_jsxs("span", { className: "inline-flex items-center gap-1 text-[10px] text-primary italic", children: [_jsx(Loader2, { className: "h-2.5 w-2.5 animate-spin" }), "Detecting type\u2026"] })) : (chips) }))] }));
}
/**
 * Capture which block has the caret, and return a function that puts it
 * back after a list rewrite. Inference writes metadata the author did not
 * type, and that re-render must not throw them out of the line they are on.
 */
export function captureCaret() {
    const active = document.activeElement;
    const focusedItemId = active instanceof HTMLTextAreaElement ? active.dataset.formItemId : null;
    const caretStart = active instanceof HTMLTextAreaElement ? active.selectionStart : null;
    const caretEnd = active instanceof HTMLTextAreaElement ? active.selectionEnd : null;
    return () => {
        if (!focusedItemId)
            return;
        window.requestAnimationFrame(() => {
            const next = document.querySelector(`textarea[data-form-item-id="${focusedItemId}"]`);
            if (!next)
                return;
            if (document.activeElement === next)
                return;
            next.focus();
            if (caretStart != null && caretEnd != null) {
                try {
                    next.setSelectionRange(caretStart, caretEnd);
                }
                catch {
                    // ignore browsers that reject on 0-length / detached nodes
                }
            }
        });
    };
}
export function freshRenderKey() {
    return (globalThis.crypto?.randomUUID?.() ??
        `rk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);
}
//# sourceMappingURL=ItemBlock.js.map